import { createSelector } from 'reselect';

import FeatureFlags from '../../constants/FeatureFlags';
import { translate } from '../../i18n';
import { BillItem } from '../../types/BillItem';
import isExists from '../../utils/isExists';
import { RootState } from '../rootReducer';
import { makeRemoteValuesByKey } from './VBConfigSelector';

export const subtotalReducerRaw = (state: RootState) =>
  state?.subtotalReducer || {};

const subtotalReducerRawData = (state: RootState) =>
  state?.subtotalReducer?.data || {};

export const makeBillDetailIsLoading = () => {
  return createSelector(subtotalReducerRaw, data => {
    const isLoading = data?.isLoading;

    return isExists(isLoading) ? isLoading : true;
  });
};

export const makeBillDetailDataByName = name => {
  return createSelector(subtotalReducerRawData, data => data?.[name]);
};

export const makeCurrentDiscounts = () => {
  return createSelector(subtotalReducerRaw, data => data.currentDiscount);
};

export const makeTotalPriceWithoutPaymentFee = () => {
  return createSelector(subtotalReducerRawData, data =>
    Math.max(0, (data?.total_price || 0) - (data?.payment_fee || 0))
  );
};

interface AttributeInfo {
  value: any;
  i18nKey: string;
  hint?: any;
  showValue: Function;
}

const attributeInfo = {
  subtotal: {
    value: _ => _?.cart_price,
    i18nKey: 'subtotal',
    showValue: (validValue, validHint) => validValue || validHint,
  },
  shippingFee: {
    value: _ => _?.shipping_fee,
    i18nKey: 'shippingFee',
    hint: _ => _?.shipping_fee_hint,
    showValue: () => true,
  },
  crossBorderShippingFee: {
    value: data => data?.cross_border_shipping_fee,
    i18nKey: 'crossBorderShippingFee',
    showValue: value => value > 0,
  },
  paymentFee: {
    value: _ => _?.payment_fee,
    i18nKey: 'paymentFee',
    showValue: (validValue, validHint) => validValue || validHint,
  },
  vcoin: {
    value: _ => _?.use_vcoin,
    i18nKey: 'vcoin-bill-detail',
    showValue: (validValue, validHint) => validValue || validHint,
  },
  eventVcoin: {
    value: _ => _?.use_event_vcoin,
    i18nKey: 'event-vcoin-bill-detail',
    showValue: (validValue, validHint) => validValue || validHint,
  },
  storageVcoin: {
    value: _ => _?.use_storage_vcoin,
    i18nKey: 'storage-vcoin-bill-detail',
    showValue: (validValue, validHint) => validValue || validHint,
  },
  coupon: {
    value: _ => _?.coupon?.coupon_value,
    i18nKey: 'coupon-bill-detail',
    showValue: (validValue, validHint) => validValue || validHint,
  },
  totalPriceWithoutPaymentFee: {
    value: _ => Math.max(0, (_?.total_price || 0) - (_?.payment_fee || 0)),
    i18nKey: 'totalPrice',
    showValue: () => true,
  },
  totalPrice: {
    value: _ => _?.total_price,
    i18nKey: 'totalPrice',
    showValue: () => true,
  },
};

export const makeBillDetail = (extraDetail = [] as string[]) => {
  return createSelector(
    subtotalReducerRawData,
    makeRemoteValuesByKey('CX_2444_cross_border_shipping_fee'),
    (data, isCX2444Enabled) => {
      return extraDetail.reduce((result: BillItem[], labelName: string) => {
        const config = attributeInfo[labelName] as AttributeInfo;
        const value: number = config?.value?.(data) || 0;
        const validValue = isExists(value) && value !== 0;
        const hint: string | false | undefined | null =
          isExists(config.hint) && config?.hint?.(data);
        const validHint = hint && hint.length > 0;

        let label = config.i18nKey;

        if (
          FeatureFlags.CX_2444_OVERSEA_SHIPPING_FEE &&
          isCX2444Enabled &&
          label === 'shippingFee'
        ) {
          label = 'internalShippingFee';
        }

        if (config.showValue(validValue, validHint)) {
          result.push({
            labelName,
            label: translate(label),
            labelMeta: hint || undefined,
            value,
          });
        }

        return result;
      }, []);
    }
  );
};
